import { supabase, handleSupabaseError, sanitizeSearchInput } from '../lib/supabase'
import { callAdminMutation } from '../lib/adminClient'
import sessionAnalytics from './sessionAnalytics'

// Generate unique filename for uploads
const generateFileName = (originalName) => {
  const timestamp = Date.now()
  const randomString = Math.random().toString(36).substring(2, 15)
  const extension = originalName.split('.').pop()
  return `${timestamp}-${randomString}.${extension}`
}

export const courseService = {
  // Get all courses with optional filters
  async getAll(filters = {}) {
    try {
      let query = supabase
        .from('courses')
        .select(`
          *,
          enrollments:enrollments(count),
          questions:questions(count)
        `)
      
      // Filter by organization if provided
      if (filters.organizationId) {
        query = query.eq('organization_id', filters.organizationId)
      }
      
      // Apply filters
      if (filters.status && filters.status !== 'All') {
        query = query.eq('status', filters.status.toLowerCase())
      }
      
      if (filters.category && filters.category !== 'All') {
        query = query.eq('category', filters.category)
      }
      
      if (filters.search) {
        const search = sanitizeSearchInput(filters.search)
        if (search) {
          query = query.or(`title.ilike.%${search}%,description.ilike.%${search}%`)
        }
      }
      
      const { data, error } = await query.order('created_at', { ascending: false })
      
      if (error) throw error
      
      // Transform data to match existing structure
      return { data: data.map(course => ({
        ...course,
        enrolled: course.enrollments?.[0]?.count || 0,
        questionCount: course.questions?.[0]?.count || 0
      })), error: null }
    } catch (error) {
      if (import.meta.env.DEV) console.error('Error fetching courses:', error)
      return { data: [], error: handleSupabaseError(error) }
    }
  },

  // Get single course by ID with full details
  async getById(id) {
    try {
      const { data, error } = await supabase
        .from('courses')
        .select(`
          *,
          questions (
            id,
            question_text,
            topic,
            order_index
          )
        `)
        .eq('id', id)
        .single()
      
      if (error) throw error
      return { data, error: null }
    } catch (error) {
      if (import.meta.env.DEV) console.error('Error fetching course:', error)
      return { data: null, error }
    }
  },

  // Get course statistics
  async getStats(courseId) {
    try {
      const { data, error } = await supabase
        .from('course_stats')
        .select('*')
        .eq('id', courseId)
        .single()
      
      if (error) throw error
      
      // Calculate completion rate
      const completionRate = data.enrolled_count > 0 
        ? Math.round((data.completed_count / data.enrolled_count) * 100)
        : 0
      
      // Get actual average time from assessment sessions
      let avgTimeMinutes = null
      try {
        // Import sessionAnalytics to avoid circular dependency
        const sessionAnalytics = await import('./sessionAnalytics')
        const metrics = await sessionAnalytics.default.getSessionMetrics(courseId)

        if (metrics?.duration) {
          avgTimeMinutes = Math.round(metrics.duration.minutes)
        }
      } catch (timeError) {
        if (import.meta.env.DEV) console.warn('Could not fetch session time data:', timeError)
        // avgTimeMinutes remains null
      }
      
      return { data: {
        enrolled: data.enrolled_count || 0,
        completed: data.completed_count || 0,
        completionRate,
        avgScore: Math.round(data.avg_score || 0),
        avgProgress: Math.round(data.avg_progress || 0),
        questionCount: data.question_count || 0,
        avgTimeMinutes // null if no real data available
      }, error: null }
    } catch (error) {
      if (import.meta.env.DEV) console.error('Error fetching course stats:', error)
      return { data: {
        enrolled: 0,
        completed: 0,
        completionRate: 0,
        avgScore: 0,
        avgProgress: 0,
        questionCount: 0,
        avgTimeMinutes: null
      }, error: handleSupabaseError(error) }
    }
  },

  // Create new course - SECURED via server-side admin verification
  async create(courseData) {
    try {
      const data = await callAdminMutation('course.create', {
        title: courseData.title,
        description: courseData.description,
        category: courseData.category,
        difficulty: courseData.difficulty,
        thumbnail_url: courseData.thumbnail_url || courseData.thumbnail,
        status: courseData.status || 'draft',
        estimated_duration: parseInt(courseData.duration) || 60,
        randomize_questions: courseData.randomize_questions || false
      })

      return { data, error: null }
    } catch (error) {
      return { data: null, error: handleSupabaseError(error) }
    }
  },

  // Update existing course - SECURED via server-side admin verification
  async update(id, courseData) {
    try {
      const data = await callAdminMutation('course.update', {
        id,
        title: courseData.title,
        description: courseData.description,
        category: courseData.category,
        difficulty: courseData.difficulty,
        thumbnail_url: courseData.thumbnail,
        status: courseData.status,
        estimated_duration: parseInt(courseData.duration) || 60,
        randomize_questions: courseData.randomize_questions
      })

      return { data, error: null }
    } catch (error) {
      return { data: null, error: handleSupabaseError(error) }
    }
  },

  // Delete course - SECURED via server-side admin verification
  async delete(id) {
    try {
      await callAdminMutation('course.delete', { id })
      return { error: null }
    } catch (error) {
      return { error: handleSupabaseError(error) }
    }
  },

  // Publish/unpublish course - SECURED via server-side admin verification
  async updateStatus(id, status) {
    try {
      const data = await callAdminMutation('course.updateStatus', { id, status })
      return { data, error: null }
    } catch (error) {
      return { data: null, error: handleSupabaseError(error) }
    }
  },

  // Get courses assigned to user's organization
  async getAssignedCourses(userEmail) {
    try {
      const { data, error } = await supabase
        .rpc('get_user_assigned_courses', { user_email: userEmail })
      
      if (error) throw error
      return data || []
    } catch (error) {
      if (import.meta.env.DEV) console.error('Error fetching assigned courses:', error)
      return []
    }
  },

  // Assign course to organization
  async assignToOrganization(courseId, organizationId, options = {}) {
    try {
      const { data, error } = await supabase
        .from('course_assignments')
        .insert([{
          course_id: courseId,
          organization_id: organizationId,
          is_mandatory: options.isMandatory || false,
          due_date: options.dueDate || null,
          assigned_by: options.assignedBy || 'admin@company.com'
        }])
        .select()
        .single()
      
      if (error) throw error
      return { data, error: null }
    } catch (error) {
      return { data: null, error: handleSupabaseError(error) }
    }
  },

  // Remove course assignment
  async removeAssignment(courseId, organizationId) {
    try {
      const { error } = await supabase
        .from('course_assignments')
        .delete()
        .eq('course_id', courseId)
        .eq('organization_id', organizationId)
      
      if (error) throw error
      return { error: null }
    } catch (error) {
      return { error: handleSupabaseError(error) }
    }
  },

  // Get course assignments
  async getCourseAssignments(courseId) {
    try {
      const { data, error } = await supabase
        .from('course_assignments')
        .select(`
          *,
          organization:organizations(id, name)
        `)
        .eq('course_id', courseId)
      
      if (error) throw error
      return data || []
    } catch (error) {
      if (import.meta.env.DEV) console.error('Error fetching course assignments:', error)
      return []
    }
  },

  // Get courses assigned directly to a learner
  async getLearnerAssignedCourses(learnerEmail) {
    try {
      const { data, error } = await supabase
        .rpc('get_learner_assigned_courses', { p_learner_email: learnerEmail })

      if (error) throw error

      // Enhance each course with calculated duration
      const coursesWithDuration = await Promise.all(
        (data || []).map(async (course) => {
          const calculatedDuration = await this.getAverageCourseDuration(course.course_id)
          return {
            ...course,
            calculated_duration: calculatedDuration
          }
        })
      )

      return coursesWithDuration
    } catch (error) {
      if (import.meta.env.DEV) console.error('Error fetching learner assigned courses:', error)
      return []
    }
  },

  // Assign course directly to a learner - SECURED via server-side admin verification
  async assignToLearner(courseId, learnerEmail, assignedBy) {
    try {
      const data = await callAdminMutation('course.assignToLearner', {
        courseId,
        learnerEmail,
        assignedBy
      })
      return { data, error: null }
    } catch (error) {
      return { data: null, error: handleSupabaseError(error) }
    }
  },

  // Remove course assignment from learner - SECURED via server-side admin verification
  async removeLearnerAssignment(courseId, learnerEmail) {
    try {
      await callAdminMutation('course.removeLearnerAssignment', {
        courseId,
        learnerEmail
      })
      return { error: null }
    } catch (error) {
      return { error: handleSupabaseError(error) }
    }
  },

  // Get average actual duration from completed sessions
  async getAverageCourseDuration(courseId) {
    try {
      // Get all completed sessions for this course
      const { data: sessions, error } = await supabase
        .from('assessment_sessions')
        .select('total_time, total_active_time, started_at, completed_at')
        .eq('course_id', courseId)
        .eq('status', 'completed')

      if (error) throw error

      // Use the centralized analytics module
      return sessionAnalytics.calculateAverageDuration(sessions)
    } catch (error) {
      if (import.meta.env.DEV) console.error('Error calculating average course duration:', error)
      return null
    }
  },

  // Get all assignments for a learner
  async getLearnerAssignments(learnerEmail) {
    try {
      const { data, error } = await supabase
        .from('learner_course_assignments')
        .select(`
          *,
          course:courses(id, title, category, difficulty)
        `)
        .eq('learner_email', learnerEmail)
      
      if (error) throw error
      return data || []
    } catch (error) {
      if (import.meta.env.DEV) console.error('Error fetching learner assignments:', error)
      return []
    }
  },

  // Get learners enrolled in a course
  async getEnrolledLearners(courseId) {
    try {
      // Use Supabase's join to get enrollments with user data
      const { data, error } = await supabase
        .from('enrollments')
        .select(`
          *,
          users!enrollments_user_email_fkey (
            id,
            email,
            name,
            department,
            role
          )
        `)
        .eq('course_id', courseId)
        .order('enrolled_at', { ascending: false })
      
      if (error) throw error
      
      if (!data || data.length === 0) {
        return []
      }
      
      // Transform data to flatten user info
      return data.map(enrollment => ({
        id: enrollment.users?.id || enrollment.id,
        email: enrollment.user_email,
        full_name: enrollment.users?.name || enrollment.user_email.split('@')[0],
        department: enrollment.users?.department || null,
        role: enrollment.users?.role || 'learner',
        status: enrollment.status,
        progress: enrollment.progress,
        // Use overall_accuracy (true competency) with fallback to final_score
        score: enrollment.overall_accuracy ?? enrollment.final_score ?? null,
        enrolled_at: enrollment.enrolled_at,
        completed_at: enrollment.completed_at
      }))
    } catch (error) {
      if (import.meta.env.DEV) console.error('Error fetching enrolled learners:', error)
      return []
    }
  },

  // Upload course thumbnail to Supabase Storage
  async uploadThumbnail(file) {
    try {
      // Validate file
      if (!file) throw new Error('No file provided')
      
      const maxSize = 5 * 1024 * 1024 // 5MB
      if (file.size > maxSize) {
        throw new Error('File size must be less than 5MB')
      }
      
      // Generate unique filename
      const fileName = generateFileName(file.name)
      const filePath = `courses/${fileName}`
      
      // Upload to Supabase Storage
      const { data, error } = await supabase.storage
        .from('course-thumbnails')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        })
      
      if (error) throw error
      
      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('course-thumbnails')
        .getPublicUrl(filePath)
      
      return { url: publicUrl, path: filePath, error: null }
    } catch (error) {
      if (import.meta.env.DEV) console.error('Error uploading thumbnail:', error)
      return { url: null, path: null, error: error.message }
    }
  },
  
  // Delete course thumbnail from Supabase Storage
  async deleteThumbnail(filePath) {
    try {
      if (!filePath) return { error: null }
      
      // Extract just the path if a full URL was provided
      const path = filePath.includes('course-thumbnails/') 
        ? filePath.split('course-thumbnails/')[1]
        : filePath
      
      const { error } = await supabase.storage
        .from('course-thumbnails')
        .remove([path])
      
      if (error) throw error
      return { error: null }
    } catch (error) {
      if (import.meta.env.DEV) console.error('Error deleting thumbnail:', error)
      return { error: error.message }
    }
  }
}